// src/fleet-stats/components/ActivityGridSection.jsx
//
// Robot × period grid. Answers "how far does each robot drive per week" in one
// screen, which every other view could only do one robot or one week at a time.
//
// The CSV deliberately exports the grid as shown rather than the whole-range
// per-robot shape the main export uses — the point of the section is the
// breakdown, so an export that flattens it away would be the wrong file.

import React, { useState } from "react";
import { Download, Grid3x3 } from "lucide-react";
import { C, CARD_BG, CARD_BORDER } from "../constants.js";
import { fmtNum } from "../utils/format.js";
import { brandButton, hoverNeon, unhoverNeon } from "./SharedUI.jsx";

const GRAINS = [
    { key: "day", label: "Day" },
    { key: "week", label: "Week" },
    { key: "month", label: "Month" },
];

// "driving h" not "hours": the file also carries uptime, and a robot powered
// on all day with no driving would otherwise look identical to one that drove
// for eight. This column is moving_time_s — time actually driving.
const METRICS = [
    { key: "km", label: "km" },
    { key: "hours", label: "driving h" },
];

function Toggle({ options, value, onChange }) {
    return (
        <div className="flex items-stretch rounded-lg overflow-hidden"
             style={{ border: "1px solid rgba(205,214,217,0.16)", backgroundColor: "#020b16" }}>
            {options.map((opt, i) => {
                const active = value === opt.key;
                return (
                    <button
                        key={opt.key}
                        onClick={() => onChange(opt.key)}
                        className="px-3 py-1.5 text-xs font-semibold transition-colors"
                        style={{
                            backgroundColor: active ? C.strongBlue : "transparent",
                            color: active ? C.offWhite : C.grey2,
                            borderLeft: i > 0 ? "1px solid rgba(205,214,217,0.12)" : "none",
                        }}
                        onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = C.neonGreen; }}
                        onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = C.grey2; }}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

/* A zero and a blank are different facts: zero means the robot reported that
   period and drove nothing, blank means it reported nothing at all. Showing
   both as "0.0" would hide robots that are simply absent. */
function Cell({ value, max, unit }) {
    if (value === 0) return <span style={{ color: C.grey3 }}>·</span>;
    const strength = max > 0 ? Math.min(value / max, 1) : 0;
    return (
        <span
            className="tabular-nums"
            style={{ color: C.offWhite, opacity: 0.55 + strength * 0.45 }}
            title={`${value.toFixed(2)} ${unit}`}
        >
            {fmtNum(value, value >= 100 ? 0 : 1)}
        </span>
    );
}

export function ActivityGridSection({ grid, grain, setGrain, metric, setMetric, onSelectRobot }) {
    const [open, setOpen] = useState(false);

    const downloadCsv = () => {
        const head = ["client", "site", "robot", "robot_id",
                      ...grid.columns.map((c) => c.key), "total", "average"];
        const lines = [head.join(",")];
        for (const r of grid.rows) {
            lines.push([
                JSON.stringify(r.client || ""), JSON.stringify(r.siteName || ""),
                JSON.stringify(r.robotName), r.robotId,
                ...r.cells.map((v) => v.toFixed(3)),
                r.total.toFixed(3), r.average.toFixed(3),
            ].join(","));
        }
        lines.push(["", "", "TOTAL", "", ...grid.totals.map((v) => v.toFixed(3)),
                    grid.grandTotal.toFixed(3), ""].join(","));
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `robot_activity_${grid.grain}_${grid.metric}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const max = grid ? grid.rows.reduce(
        (m, r) => r.cells.reduce((mm, v) => Math.max(mm, v), m), 0) : 0;

    return (
        <div>
            <button
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                style={{ color: C.grey1 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                onMouseLeave={(e) => (e.currentTarget.style.color = C.grey1)}
            >
                <Grid3x3 size={16} />
                {open ? "Hide" : "Show"} distance per robot, per period
            </button>

            {open && !grid && (
                <div className="mt-3 text-sm" style={{ color: C.grey2 }}>
                    No activity in this selection.
                </div>
            )}

            {open && grid && (
                <div className="mt-3">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                        <Toggle options={GRAINS} value={grain} onChange={setGrain} />
                        <Toggle options={METRICS} value={metric} onChange={setMetric} />
                        <span className="text-xs" style={{ color: C.grey2 }}>
                            {grid.robotCount} robot{grid.robotCount === 1 ? "" : "s"} ·{" "}
                            {grid.columns.length} {grain}
                            {grid.columns.length === 1 ? "" : "s"} · values in {grid.metric === "hours" ? "driving hours" : "km"}
                        </span>
                        <button
                            onClick={downloadCsv}
                            className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ml-auto"
                            style={brandButton()}
                            onMouseEnter={hoverNeon}
                            onMouseLeave={unhoverNeon}
                        >
                            <Download size={14} /> CSV
                        </button>
                    </div>

                    <div className="rounded-xl overflow-hidden"
                         style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
                        <div className="overflow-auto" style={{ maxHeight: 520 }}>
                            <table className="text-sm" style={{ minWidth: "100%" }}>
                                <thead style={{ backgroundColor: C.darkBlue }}>
                                    <tr style={{ color: C.grey2 }}>
                                        <th className="px-3 py-2 font-medium text-left sticky left-0 z-10"
                                            style={{ backgroundColor: C.darkBlue }}>Robot</th>
                                        <th className="px-3 py-2 font-medium text-left">Site</th>
                                        {grid.columns.map((c) => (
                                            <th key={c.key} className="px-3 py-2 font-medium text-right whitespace-nowrap"
                                                title={c.key}>{c.label}</th>
                                        ))}
                                        <th className="px-3 py-2 font-medium text-right">Total</th>
                                        <th className="px-3 py-2 font-medium text-right whitespace-nowrap"
                                            title={`Average over the ${grain}s this robot was active, not over every column`}>
                                            Avg/{grain}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grid.rows.map((r) => (
                                        <tr key={r.robotId}
                                            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                                            <td className="px-3 py-2 sticky left-0"
                                                style={{ backgroundColor: "#03202a" }}>
                                                <button
                                                    onClick={() => onSelectRobot && onSelectRobot(r.robotName)}
                                                    className="transition-colors text-left whitespace-nowrap"
                                                    style={{ color: C.offWhite }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
                                                    onMouseLeave={(e) => (e.currentTarget.style.color = C.offWhite)}
                                                >
                                                    {r.robotName}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap" style={{ color: C.grey2 }}>
                                                {r.siteName}
                                            </td>
                                            {r.cells.map((v, i) => (
                                                <td key={grid.columns[i].key} className="px-3 py-2 text-right">
                                                    <Cell value={v} max={max} unit={grid.unit} />
                                                </td>
                                            ))}
                                            <td className="px-3 py-2 text-right tabular-nums font-semibold"
                                                style={{ color: C.offWhite }}>
                                                {fmtNum(r.total, 1)}
                                            </td>
                                            <td className="px-3 py-2 text-right tabular-nums"
                                                style={{ color: C.grey1 }}>
                                                {fmtNum(r.average, 1)}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ borderTop: "1px solid rgba(255,255,255,0.18)",
                                                 backgroundColor: "rgba(255,255,255,0.02)" }}>
                                        <td className="px-3 py-2 sticky left-0 font-semibold"
                                            style={{ backgroundColor: "#03202a", color: C.grey1 }}>Total</td>
                                        <td />
                                        {grid.totals.map((v, i) => (
                                            <td key={grid.columns[i].key}
                                                className="px-3 py-2 text-right tabular-nums font-semibold"
                                                style={{ color: C.grey1 }}>
                                                {fmtNum(v, 1)}
                                            </td>
                                        ))}
                                        <td className="px-3 py-2 text-right tabular-nums font-semibold"
                                            style={{ color: C.offWhite }}>
                                            {fmtNum(grid.grandTotal, 1)}
                                        </td>
                                        <td />
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {grid.hiddenCount > 0 && (
                        <div className="text-xs mt-2" style={{ color: C.grey2 }}>
                            Showing the busiest {grid.rows.length} robots.{" "}
                            {grid.hiddenCount} more account for{" "}
                            {fmtNum(grid.hiddenTotal, 1)} {grid.unit} — narrow by site or
                            robot to see them, or use the CSV.
                        </div>
                    )}
                    <div className="text-xs mt-2" style={{ color: C.grey2 }}>
                        · means the robot reported nothing that {grain}. Avg is over the{" "}
                        {grain}s a robot was active, not over every column.
                    </div>
                </div>
            )}
        </div>
    );
}
